//
//  SDKDTTokenMerger.ts
//  Supernova SDK
//
//  Created by Jiri Trecak.
//
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Imports
import { ALL_TOKEN_TYPES } from "../../../model/enums/SDKTokenType";
import { replaceOriginReferencePersistentIdByIds, replaceReferencedTokenByIds, } from "../../../model/tokens/SDKTokenValue";
import { TokenUtils } from "../../../utils/TokenUtils";
import { v4 as uuidv4 } from "uuid";
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Tool implementation
/** Utility allowing merging of two pools of tokens */
export class DTTokenMerger {
    static buildKey(path, name) {
        return TokenUtils.buildKey(path, name);
    }
    static buildReference(path, name) {
        return TokenUtils.buildReference(path, name);
    }
    buildNodeMap(nodes) {
        const result = new Map();
        for (const node of nodes) {
            // Possibly filter out tokens with styles associated with them?
            result.set(node.key, node);
        }
        return result;
    }
    convertBaseTokensToProcessedNode(tokens, groups) {
        // @ts-expect-error TS(2322): Type '{ token: AnyToken; key: string; originalType... Remove this comment to see the full error message
        return tokens.map((t) => {
            const path = TokenUtils.buildPath(t, groups);
            const key = TokenUtils.buildKey(path, t.name);
            return {
                token: t,
                key,
                originalType: undefined, // Note this is correct - we don't need the original type anywhere in merging
                path,
            };
        });
    }
    makeTokensDiff(existing, extracted, groups) {
        const toUpdate = [];
        const toCreate = [];
        const toDelete = [];
        const toCreateOrUpdate = [];
        const existingAsProcessedNodes = this.convertBaseTokensToProcessedNode(existing, groups);
        const existingMap = this.buildNodeMap(existingAsProcessedNodes);
        const extractedMap = this.buildNodeMap(extracted);
        const idInVersionToIgnore = [];
        // Existing: font-sizing/font-size/fontsizes/body
        // Extracted: measure/font-size/fontsizes/body
        // Maybe, instead we should run a migration to rename origin.ids?
        // Pay attention to group names, if they renamed - mapping will fail
        // I.e. now group named Measure, if it would be renamed to Dimension
        // Reimport from TS will remove ids
        const idsToRemap = new Map();
        for (const node of extracted) {
            const oldToken = TokenUtils.getTokenByKeyOrFallbackKeys(node, existingMap);
            if (oldToken) {
                // FIXME:
                // Later when we build token that references `node.token`, we got updated id, as node.token was a reference.
                // Now we don't keep it as ref, so need to update id in all tokens that reference it.
                idsToRemap.set(node.token.id, oldToken.token.id);
                node.token.id = oldToken.token.id;
                node.token.idInVersion = oldToken.token.idInVersion;
                toUpdate.push(node);
                // TODO:fix-sdk-eslint
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
                idInVersionToIgnore.push(oldToken.token.idInVersion);
            }
            else {
                const newId = uuidv4();
                idsToRemap.set(node.token.id, newId);
                node.token.id = newId;
                toCreate.push(node);
            }
            toCreateOrUpdate.push(node);
        }
        for (const [oldId, newId] of idsToRemap) {
            for (const node of toCreateOrUpdate) {
                replaceReferencedTokenByIds(node.token, oldId, newId);
                replaceOriginReferencePersistentIdByIds(node.token, oldId, newId);
                // TODO: Fix inside Typography.fontSize
                TokenUtils.replaceIdForComplexTokens(node.token, oldId, newId);
            }
        }
        const extractedMapWithoutMatchedTokens = this.buildNodeMap([...extractedMap.values()].filter(
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
        (t) => !idInVersionToIgnore.includes(t.token.idInVersion)));
        for (const node of existingAsProcessedNodes) {
            // TODO:fix-sdk-eslint
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
            if (idInVersionToIgnore.includes(node.token.idInVersion)) {
                // TODO:fix-sdk-eslint
                // eslint-disable-next-line no-continue
                continue;
            }
            const isInExtractedMap = !!TokenUtils.getTokenByKeyOrBackwardKey(node, extractedMapWithoutMatchedTokens);
            if (!isInExtractedMap) {
                toDelete.push(node);
                // Any tokens and theme overrides that were imported from Figma are unlinked (turned into SN tokens)
                // If preciseCopy = false, we should still unlink Figma tokens, that do not match any tokens from extracted from payload
                if (node.token.origin) {
                    node.token.origin = null;
                    toUpdate.push(node);
                    toCreateOrUpdate.push(node);
                }
            }
        }
        // Sort tokens to delete so tokens that are aliased deleted after tokens that alias them
        // Complex token first, as they may reference simple tokens
        // Tokens that reference other tokens first
        toDelete.sort((a, b) => {
            // TODO:fix-sdk-eslint
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const aValue = a.token.value;
            // TODO:fix-sdk-eslint
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const bValue = a.token.value;
            const aIndex = ALL_TOKEN_TYPES.indexOf(a.token.tokenType);
            const bIndex = ALL_TOKEN_TYPES.indexOf(b.token.tokenType);
            const diff = bIndex - aIndex;
            return diff;
        });
        toDelete.sort((a, b) => {
            const aValue = a.token.value;
            const bValue = a.token.value;
            const aIndex = ALL_TOKEN_TYPES.indexOf(a.token.tokenType);
            const bIndex = ALL_TOKEN_TYPES.indexOf(b.token.tokenType);
            const diff = bIndex - aIndex;
            const aRefsB = (aValue === null || aValue === void 0 ? void 0 : aValue.referencedTokenId) === b.token.id;
            const bRefsA = (bValue === null || bValue === void 0 ? void 0 : bValue.referencedTokenId) === a.token.id;
            // TODO:fix-sdk-eslint
            // eslint-disable-next-line no-nested-ternary
            return diff !== 0 ? diff : aRefsB ? -1 : bRefsA ? 1 : 0;
        });
        return {
            toUpdate,
            toCreate,
            toDelete,
            toCreateOrUpdate,
        };
    }
}
